/* eslint-disable prettier/prettier */
import { ContentPaste, OpenInNewRounded, PersonPin, Pets, Star, Transgender, Tune, Wc } from '@mui/icons-material'
import React, { FC, useEffect, useRef } from 'react'

import {
  Box,
  Button,
  Card,
  CircularProgress,
  Fab,
  IconButton,
  InputAdornment,
  Pagination,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'

import { Close, Search as SearchIcon } from '@mui/icons-material'
import { useLocation, useNavigate } from 'react-router-dom'
import { accountCanEdit } from '../../domain/Account/accountCanEdit'
import {
  getReadableAnimalAdoptionStatus,
  getReadableAnimalAdoptionStep,
  getReadableCannotBeAdoptedStatus,
  getReadableSex,
  getReadableSpecies,
} from '../../domain/Animal/AnimalDisplay'
import { AnimalFilters } from '../../domain/Animal/AnimalFiltersType'
import { isAnimalOfferPublished } from '../../domain/Animal/isAnimalOfferPublished'
import { BooleanOrAll, BooleanOrUnknown } from '../../domain/enums/Boolean.enum'
import { AnimalsClient } from '../../interactors/clients/AnimalsClient'
import {
  AnimalAdoptionDtoCannotBeAdoptedStatus,
  AnimalAdoptionDtoStatus,
  AnimalAdoptionDtoStep,
  AnimalDtoSex,
  AnimalDtoSpecies,
  AnimalSearchItemDto,
} from '../../interactors/gen/backendClient'
import { useAccountsStore } from '../../store/AccountsStore'
import { useAnimalStore } from '../../store/AnimalStore'
import { useFetchOrRefreshAnimalsOnMount } from '../../store/useFetchOrRefreshAnimalsOnMount'
import { useFetchOrRefreshMembersOnMount } from '../../store/useFetchOrRefreshMembersOnMount'
import { getReadableBooleanOrUnknown } from '../../utils/getReadableBooleanOrUnknown'
import { useComponentScrollRestoration } from '../../utils/hooks/useScrollRestoration'
import { PATHS } from '../PATHS'
import { CircularProgressPanel } from '../common/CircularProgressPanel'
import { FilterList } from '../common/FilterList'
import { FilterListItem } from '../common/FilterListItem'
import { GettingStartedPanel } from '../common/GettingStartedPanel'
import { OverviewFilterDialog, OverviewFilterSection } from '../common/OverviewFilterDialog'
import {
  overviewActionButtonSx,
  overviewFilterCountBadgeSx,
  overviewTabSx,
  overviewTabsSx,
} from '../common/overviewStyles'
import { searchFieldSx } from '../common/searchFieldSx'
import { colors, spacingItem } from '../theme'
import { AnimalsTable, SortColumn, SortDirection } from './AnimalsTable'

export type AnimalAdoptionDtoStepWithEmpty = AnimalAdoptionDtoStep | 'empty'
export type AnimalAdoptionDtoCannotBeAdoptedStatusWithEmpty = AnimalAdoptionDtoCannotBeAdoptedStatus | 'empty'

const areArraysEqual = (firstArray: string[], secondArray: string[]) =>
  firstArray.length === secondArray.length && firstArray.every((value, index) => value === secondArray[index])

const defaultStepFilter = [...Object.values(AnimalAdoptionDtoStep), 'empty'] as AnimalAdoptionDtoStepWithEmpty[]

export const AnimalListScreen: FC = () => {
  const scrollRef = useRef<HTMLElement | null>(null)
  useComponentScrollRestoration(scrollRef, 'animal-list')
  const accountsStore = useAccountsStore()

  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const getQueryParamOrDefault = <T extends string | null>(paramValue: string | null, defaultValue: T) => {
    if (paramValue == null || paramValue === '') {
      return defaultValue
    }
    return paramValue as T
  }
  const getArrayQueryParamOrDefault = (paramValue: string | null | undefined, defaultValue: string[]) => {
    const values = paramValue?.split('|')
    if (values && values.length === 1 && values[0] === '') {
      return defaultValue
    }
    return values ?? defaultValue
  }

  const [selectedAnimals, setSelectedAnimals] = React.useState<AnimalSearchItemDto[]>([])
  const associationPublicPageLink = accountsStore.connectedCustomer?.id
    ? `https://petso.fr/associations/${accountsStore.connectedCustomer.id}`
    : 'https://petso.fr'

  // Filters
  const [myAnimalsOnly, setMyAnimalsOnly] = React.useState(queryParams.get('myAnimalsOnly') === 'true')
  const [hasIdentificationNumberFilter, setHasIdentificationNumberFilter] = React.useState(
    getQueryParamOrDefault<BooleanOrAll>(queryParams.get('hasIdentificationNumberFilter'), BooleanOrAll.All)
  )
  const [sterilizedOrNeuteredFilter, setSterilizedOrNeuteredFilter] = React.useState<BooleanOrUnknown[]>(
    getArrayQueryParamOrDefault(queryParams.get('sterilizedOrNeuteredFilter'), []) as BooleanOrUnknown[]
  )
  const [adoptionStatusFilter, setAdoptionStatusFilter] = React.useState<AnimalAdoptionDtoStatus[]>(
    getArrayQueryParamOrDefault(queryParams.get('adoptionStatusFilter'), [
      AnimalAdoptionDtoStatus.Adoptable,
      AnimalAdoptionDtoStatus.InTheProcessOfBeingAdoptable,
      AnimalAdoptionDtoStatus.CannotBeAdopted,
    ]) as AnimalAdoptionDtoStatus[]
  )
  const [speciesFilter, setSpeciesFilter] = React.useState<AnimalDtoSpecies[]>(
    getArrayQueryParamOrDefault(queryParams.get('speciesFilter'), []) as AnimalDtoSpecies[]
  )
  /* const [hasExitDateFilter, setHasExitDateFilter] = React.useState<boolean | null>(null) */
  const [vpaDateFilter, setVpaDateFilter] = React.useState<'past' | 'future' | 'empty' | null>(
    getQueryParamOrDefault<'past' | 'future' | 'empty' | null>(queryParams.get('vpaDateFilter'), null)
  )
  const [sexFilter, setSexFilter] = React.useState<AnimalDtoSex | null>(
    getQueryParamOrDefault<AnimalDtoSex | null>(queryParams.get('sexFilter'), null)
  )
  const [stepFilter, setStepFilter] = React.useState<AnimalAdoptionDtoStepWithEmpty[]>(
    getArrayQueryParamOrDefault(queryParams.get('stepFilter'), [
      ...Object.values(AnimalAdoptionDtoStep),
      'empty',
    ]) as AnimalAdoptionDtoStepWithEmpty[]
  )
  const [cannotBeAdoptedStatusFilter, setCannotBeAdoptedStatusFilter] = React.useState<
    AnimalAdoptionDtoCannotBeAdoptedStatusWithEmpty[]
  >(
    getArrayQueryParamOrDefault(queryParams.get('cannotBeAdoptedStatusFilter'), [
      AnimalAdoptionDtoCannotBeAdoptedStatus.BeingSocialized,
      AnimalAdoptionDtoCannotBeAdoptedStatus.InCare,
      AnimalAdoptionDtoCannotBeAdoptedStatus.InGestation,
      AnimalAdoptionDtoCannotBeAdoptedStatus.InLactation,
      AnimalAdoptionDtoCannotBeAdoptedStatus.Released,
      AnimalAdoptionDtoCannotBeAdoptedStatus.LongTermWithHostedFamily,
      'empty',
    ]) as AnimalAdoptionDtoCannotBeAdoptedStatusWithEmpty[]
  )
  const createFilterObject = (): AnimalFilters => {
    return {
      myAnimalsOnly,
      hasIdentificationNumberFilter,
      sterilizedOrNeuteredFilter,
      adoptionStatusFilter,
      stepFilter,
      cannotBeAdoptedStatusFilter,
      hasExitDateFilter: null,
      vpaDateFilter,
      speciesFilter,
      sexFilter,
    }
  }
  const [selectedTab, setSelectedTab] = React.useState<string>(queryParams.get('selectedTab') ?? 'followed')
  const tabsRef = useRef<any>(null)

  const [sortColumn, setSortColumn] = React.useState<SortColumn>(
    (queryParams.get('sortColumn') ?? 'insights') as SortColumn
  )
  const [sortDirection, setSortDirection] = React.useState<SortDirection>(
    (queryParams.get('sortDirection') ?? 'asc') as SortDirection
  )

  const [isFiltersOpen, setIsFiltersOpen] = React.useState(false)
  const [page, setPage] = React.useState(parseInt(queryParams.get('page') || '1', 10))
  const [assoHasAnimals, setAssoHasAnimals] = React.useState<boolean>(true)
  const [searchQuery, setSearchQuery] = React.useState(queryParams.get('search') || '')
  const [appliedSearchQuery, setAppliedSearchQuery] = React.useState(queryParams.get('search') || '')
  const maxAnimalPerPage = 50
  const maxPublishedAnimalPerPage = 9999
  const isSearchDirty = searchQuery.trim() !== appliedSearchQuery
  const hasAppliedSearch = appliedSearchQuery !== ''

  const animalStore = useAnimalStore()
  const navigate = useNavigate()

  const account = accountsStore.connectedAccount

  useEffect(() => {
    // Keep the selected tab roughly centered on mobile so left/right tabs remain reachable.
    const tabsEl = tabsRef.current as HTMLElement | null
    if (!tabsEl) return
    const selectedEl = tabsEl.querySelector('[role="tab"][aria-selected="true"]') as HTMLElement | null
    if (!selectedEl) return
    try {
      selectedEl.scrollIntoView({ behavior: 'auto', inline: 'center', block: 'nearest' })
    } catch {
      selectedEl.scrollIntoView()
    }
  }, [selectedTab])

  useEffect(() => {
    const searchParams = new URLSearchParams()
    searchParams.set('page', page.toString())
    searchParams.set('search', appliedSearchQuery)
    searchParams.set('myAnimalsOnly', myAnimalsOnly.toString())
    searchParams.set('hasIdentificationNumberFilter', hasIdentificationNumberFilter)
    searchParams.set('sterilizedOrNeuteredFilter', sterilizedOrNeuteredFilter.join('|'))
    searchParams.set('adoptionStatusFilter', adoptionStatusFilter.join('|'))
    searchParams.set('stepFilter', stepFilter.join('|'))
    searchParams.set('cannotBeAdoptedStatusFilter', cannotBeAdoptedStatusFilter.join('|'))
    searchParams.set('vpaDateFilter', vpaDateFilter ?? '')
    searchParams.set('speciesFilter', speciesFilter.join('|'))
    searchParams.set('sexFilter', sexFilter ?? '')
    searchParams.set('sortColumn', sortColumn)
    searchParams.set('sortDirection', sortDirection)
    searchParams.set('selectedTab', selectedTab)
    // Add more parameters as needed
    navigate(`?${searchParams.toString()}`, { replace: true })
  }, [
    page,
    appliedSearchQuery,
    myAnimalsOnly,
    hasIdentificationNumberFilter,
    sterilizedOrNeuteredFilter,
    adoptionStatusFilter,
    stepFilter,
    cannotBeAdoptedStatusFilter,
    speciesFilter,
    sexFilter,
    sortColumn,
    sortDirection,
    selectedTab,
  ])

  const isFirstRender = useRef(true)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false // Mark as rendered
      return // Skip this effect on first render
    }

    setPage(1) // Reset page to 1 whenever filter values change
  }, [
    myAnimalsOnly,
    hasIdentificationNumberFilter,
    sterilizedOrNeuteredFilter,
    adoptionStatusFilter,
    stepFilter,
    cannotBeAdoptedStatusFilter,
    vpaDateFilter,
    speciesFilter,
    sexFilter,
  ])

  useEffect(() => {
    AnimalsClient.countAllAnimal().then((count) => {
      if (count > 0) {
        setAssoHasAnimals(true)
      } else {
        setAssoHasAnimals(false)
      }
    })
  }, [])

  const onChangeSearch = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const nextValue = e.target.value
    setSearchQuery(nextValue)
    if (nextValue.trim() === '' && appliedSearchQuery !== '') {
      setAppliedSearchQuery('')
      setPage(1)
    }
  }

  useFetchOrRefreshMembersOnMount()
  useFetchOrRefreshAnimalsOnMount(
    page,
    selectedTab === 'published' ? maxPublishedAnimalPerPage : maxAnimalPerPage,
    createFilterObject(),
    appliedSearchQuery,
    sortColumn,
    sortDirection
  )

  const applyTabPreset = (tabValue: string) => {
    let adoptionFilterValues: AnimalAdoptionDtoStatus[] = []
    let cannotBeAdoptedFilterValues: AnimalAdoptionDtoCannotBeAdoptedStatusWithEmpty[] = []
    switch (tabValue) {
      case 'all':
        adoptionFilterValues = []
        cannotBeAdoptedFilterValues = [...Object.values(AnimalAdoptionDtoCannotBeAdoptedStatus), 'empty']
        break
      case 'followed':
        adoptionFilterValues = [
          AnimalAdoptionDtoStatus.Adoptable,
          AnimalAdoptionDtoStatus.InTheProcessOfBeingAdoptable,
          AnimalAdoptionDtoStatus.CannotBeAdopted,
        ]
        cannotBeAdoptedFilterValues = [
          AnimalAdoptionDtoCannotBeAdoptedStatus.BeingSocialized,
          AnimalAdoptionDtoCannotBeAdoptedStatus.InCare,
          AnimalAdoptionDtoCannotBeAdoptedStatus.InGestation,
          AnimalAdoptionDtoCannotBeAdoptedStatus.InLactation,
          AnimalAdoptionDtoCannotBeAdoptedStatus.Released,
          AnimalAdoptionDtoCannotBeAdoptedStatus.LongTermWithHostedFamily,
          'empty',
        ]
        break
      case 'to-be-filled':
        adoptionFilterValues = [AnimalAdoptionDtoStatus.ToBeFilled]
        cannotBeAdoptedFilterValues = []
        break
      case 'archived':
        adoptionFilterValues = [AnimalAdoptionDtoStatus.HasBeenAdopted, AnimalAdoptionDtoStatus.CannotBeAdopted]
        cannotBeAdoptedFilterValues = [
          AnimalAdoptionDtoCannotBeAdoptedStatus.Dead,
          AnimalAdoptionDtoCannotBeAdoptedStatus.TransferToAnotherAssociation,
          AnimalAdoptionDtoCannotBeAdoptedStatus.Lost,
        ]
        break
      case 'published':
        adoptionFilterValues = []
        cannotBeAdoptedFilterValues = [...Object.values(AnimalAdoptionDtoCannotBeAdoptedStatus), 'empty']
        break
    }
    setAdoptionStatusFilter(adoptionFilterValues)
    setCannotBeAdoptedStatusFilter(cannotBeAdoptedFilterValues)
  }

  const handleTabChange = (_: unknown, newValue: string) => {
    setSelectedTab(newValue)
    applyTabPreset(newValue)
  }

  const getDefaultFiltersForTab = (tabValue: string) => {
    switch (tabValue) {
      case 'all':
        return {
          adoptionStatusFilter: [] as AnimalAdoptionDtoStatus[],
          cannotBeAdoptedStatusFilter: [
            ...Object.values(AnimalAdoptionDtoCannotBeAdoptedStatus),
            'empty',
          ] as AnimalAdoptionDtoCannotBeAdoptedStatusWithEmpty[],
        }
      case 'to-be-filled':
        return {
          adoptionStatusFilter: [AnimalAdoptionDtoStatus.ToBeFilled] as AnimalAdoptionDtoStatus[],
          cannotBeAdoptedStatusFilter: [] as AnimalAdoptionDtoCannotBeAdoptedStatusWithEmpty[],
        }
      case 'archived':
        return {
          adoptionStatusFilter: [
            AnimalAdoptionDtoStatus.HasBeenAdopted,
            AnimalAdoptionDtoStatus.CannotBeAdopted,
          ] as AnimalAdoptionDtoStatus[],
          cannotBeAdoptedStatusFilter: [
            AnimalAdoptionDtoCannotBeAdoptedStatus.Dead,
            AnimalAdoptionDtoCannotBeAdoptedStatus.TransferToAnotherAssociation,
            AnimalAdoptionDtoCannotBeAdoptedStatus.Lost,
          ] as AnimalAdoptionDtoCannotBeAdoptedStatusWithEmpty[],
        }
      case 'published':
        return {
          adoptionStatusFilter: [] as AnimalAdoptionDtoStatus[],
          cannotBeAdoptedStatusFilter: [
            ...Object.values(AnimalAdoptionDtoCannotBeAdoptedStatus),
            'empty',
          ] as AnimalAdoptionDtoCannotBeAdoptedStatusWithEmpty[],
        }
      case 'followed':
      default:
        return {
          adoptionStatusFilter: [
            AnimalAdoptionDtoStatus.Adoptable,
            AnimalAdoptionDtoStatus.InTheProcessOfBeingAdoptable,
            AnimalAdoptionDtoStatus.CannotBeAdopted,
          ] as AnimalAdoptionDtoStatus[],
          cannotBeAdoptedStatusFilter: [
            AnimalAdoptionDtoCannotBeAdoptedStatus.BeingSocialized,
            AnimalAdoptionDtoCannotBeAdoptedStatus.InCare,
            AnimalAdoptionDtoCannotBeAdoptedStatus.InGestation,
            AnimalAdoptionDtoCannotBeAdoptedStatus.InLactation,
            AnimalAdoptionDtoCannotBeAdoptedStatus.Released,
            AnimalAdoptionDtoCannotBeAdoptedStatus.LongTermWithHostedFamily,
            'empty',
          ] as AnimalAdoptionDtoCannotBeAdoptedStatusWithEmpty[],
        }
    }
  }

  const resetFilters = () => {
    const defaultFilters = getDefaultFiltersForTab(selectedTab)
    setMyAnimalsOnly(false)
    setHasIdentificationNumberFilter(BooleanOrAll.All)
    setSterilizedOrNeuteredFilter([])
    setAdoptionStatusFilter(defaultFilters.adoptionStatusFilter)
    setStepFilter(defaultStepFilter)
    setCannotBeAdoptedStatusFilter(defaultFilters.cannotBeAdoptedStatusFilter)
    setVpaDateFilter(null)
    setSpeciesFilter([])
    setSexFilter(null)
  }

  const defaultFilters = getDefaultFiltersForTab(selectedTab)
  const activeFiltersCount = [
    myAnimalsOnly,
    hasIdentificationNumberFilter !== BooleanOrAll.All,
    sterilizedOrNeuteredFilter.length > 0,
    !areArraysEqual(adoptionStatusFilter, defaultFilters.adoptionStatusFilter),
    !areArraysEqual(stepFilter, defaultStepFilter),
    !areArraysEqual(cannotBeAdoptedStatusFilter, defaultFilters.cannotBeAdoptedStatusFilter),
    vpaDateFilter !== null,
    speciesFilter.length > 0,
    sexFilter !== null,
  ].filter(Boolean).length

  const handleApplySearch = (nextQuery?: string) => {
    const query = (nextQuery ?? searchQuery).trim()
    setSearchQuery(query)
    setAppliedSearchQuery(query)
    setPage(1)
    if (query !== '' && selectedTab !== 'published') {
      handleTabChange(null, 'all')
    }
  }

  const handleClearSearch = () => {
    setSearchQuery('')
    if (appliedSearchQuery !== '') {
      setAppliedSearchQuery('')
      setPage(1)
    }
  }

  useEffect(() => {
    const nextSelectedTab = new URLSearchParams(location.search).get('selectedTab')
    if (nextSelectedTab != null && nextSelectedTab !== selectedTab) {
      setSelectedTab(nextSelectedTab)
      applyTabPreset(nextSelectedTab)
    }
  }, [location.search])

  const displayedAnimals =
    selectedTab === 'published'
      ? animalStore.animals.filter((animal) => isAnimalOfferPublished(animal))
      : animalStore.animals

  const FiltersBar = () => {
    return (
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' } }}>
        <OverviewFilterSection>
          <FilterList
            label="Résponsable"
            icon={<Star />}
            onReset={() => setMyAnimalsOnly(false)}
            resetDisabled={!myAnimalsOnly}
          >
            <FilterListItem
              label="Mes animaux"
              isSelected={myAnimalsOnly}
              toggleSelected={() => setMyAnimalsOnly(!myAnimalsOnly)}
            />
          </FilterList>
        </OverviewFilterSection>

        <OverviewFilterSection>
          <FilterList
            label="Identification"
            icon={<ContentPaste />}
            onReset={() => setHasIdentificationNumberFilter(BooleanOrAll.All)}
            resetDisabled={hasIdentificationNumberFilter === BooleanOrAll.All}
          >
            <FilterListItem
              label="Immatriculé"
              isSelected={hasIdentificationNumberFilter === BooleanOrAll.True}
              toggleSelected={() => {
                if (hasIdentificationNumberFilter === BooleanOrAll.True) {
                  setHasIdentificationNumberFilter(BooleanOrAll.All)
                } else {
                  setHasIdentificationNumberFilter(BooleanOrAll.True)
                }
              }}
            />
            <FilterListItem
              label="Manquante"
              isSelected={hasIdentificationNumberFilter === BooleanOrAll.False}
              toggleSelected={() => {
                if (hasIdentificationNumberFilter === BooleanOrAll.False) {
                  setHasIdentificationNumberFilter(BooleanOrAll.All)
                } else {
                  setHasIdentificationNumberFilter(BooleanOrAll.False)
                }
              }}
            />
          </FilterList>
        </OverviewFilterSection>

        <OverviewFilterSection>
          <FilterList
            label="Sterilisée/Castré"
            icon={<Transgender />}
            onReset={() => setSterilizedOrNeuteredFilter([])}
            resetDisabled={sterilizedOrNeuteredFilter.length === 0}
          >
            {Object.values(BooleanOrUnknown).map((componentStatus) => {
              return (
                <FilterListItem
                  key={componentStatus}
                  label={getReadableBooleanOrUnknown(componentStatus)}
                  isSelected={sterilizedOrNeuteredFilter.includes(componentStatus)}
                  toggleSelected={() => {
                    if (sterilizedOrNeuteredFilter.includes(componentStatus)) {
                      const newFilter = sterilizedOrNeuteredFilter.filter((aStatus) => aStatus !== componentStatus)
                      setSterilizedOrNeuteredFilter(newFilter)
                    } else {
                      setSterilizedOrNeuteredFilter([...sterilizedOrNeuteredFilter, componentStatus])
                    }
                  }}
                />
              )
            })}
          </FilterList>
        </OverviewFilterSection>

        <OverviewFilterSection>
          <FilterList
            label="Espèce"
            icon={<Pets />}
            onReset={() => setSpeciesFilter([])}
            resetDisabled={speciesFilter.length === 0}
          >
            {accountsStore.connectedCustomer?.preferences.species.map((componentStatus) => {
              return (
                <FilterListItem
                  key={componentStatus}
                  label={getReadableSpecies(componentStatus)}
                  isSelected={speciesFilter.includes(componentStatus)}
                  toggleSelected={() => {
                    if (speciesFilter.includes(componentStatus)) {
                      const newFilter = speciesFilter.filter((aStatus) => aStatus !== componentStatus)
                      setSpeciesFilter(newFilter)
                    } else {
                      setSpeciesFilter([...speciesFilter, componentStatus])
                    }
                  }}
                />
              )
            })}
          </FilterList>
        </OverviewFilterSection>

        <OverviewFilterSection>
          <FilterList label="Sexe" icon={<Wc />} onReset={() => setSexFilter(null)} resetDisabled={sexFilter === null}>
            {Object.values(AnimalDtoSex).map((componentStatus) => (
              <FilterListItem
                key={componentStatus}
                label={getReadableSex(componentStatus)}
                isSelected={sexFilter === componentStatus}
                toggleSelected={() => {
                  if (sexFilter === componentStatus) {
                    setSexFilter(null)
                  } else {
                    setSexFilter(componentStatus)
                  }
                }}
              />
            ))}
          </FilterList>
        </OverviewFilterSection>

        <OverviewFilterSection>
          <FilterList
            label="Post-visite"
            icon={<PersonPin />}
            onReset={() => setVpaDateFilter(null)}
            resetDisabled={vpaDateFilter === null}
          >
            <FilterListItem
              label="Passée"
              isSelected={vpaDateFilter === 'past'}
              toggleSelected={() => setVpaDateFilter(vpaDateFilter === 'past' ? null : 'past')}
            />
            <FilterListItem
              label="À venir"
              isSelected={vpaDateFilter === 'future'}
              toggleSelected={() => setVpaDateFilter(vpaDateFilter === 'future' ? null : 'future')}
            />
            <FilterListItem
              label="Vide"
              isSelected={vpaDateFilter === 'empty'}
              toggleSelected={() => setVpaDateFilter(vpaDateFilter === 'empty' ? null : 'empty')}
            />
          </FilterList>
        </OverviewFilterSection>

        <OverviewFilterSection>
          <FilterList
            label="Statut"
            icon={<PersonPin />}
            onReset={() => {
              setAdoptionStatusFilter(defaultFilters.adoptionStatusFilter)
              setStepFilter(defaultStepFilter)
              setCannotBeAdoptedStatusFilter(defaultFilters.cannotBeAdoptedStatusFilter)
            }}
            resetDisabled={
              areArraysEqual(adoptionStatusFilter, defaultFilters.adoptionStatusFilter) &&
              areArraysEqual(stepFilter, defaultStepFilter) &&
              areArraysEqual(cannotBeAdoptedStatusFilter, defaultFilters.cannotBeAdoptedStatusFilter)
            }
          >
            {Object.values(AnimalAdoptionDtoStatus).map((componentStatus) => {
              return (
                <FilterListItem
                  key={componentStatus}
                  label={getReadableAnimalAdoptionStatus(componentStatus)}
                  isSelected={adoptionStatusFilter.includes(componentStatus)}
                  toggleSelected={() => {
                    if (adoptionStatusFilter.includes(componentStatus)) {
                      if (componentStatus === AnimalAdoptionDtoStatus.CannotBeAdopted) {
                        setCannotBeAdoptedStatusFilter(Object.values(AnimalAdoptionDtoCannotBeAdoptedStatus))
                      }
                      if (componentStatus === AnimalAdoptionDtoStatus.HasBeenAdopted) {
                        setStepFilter(Object.values(AnimalAdoptionDtoStep))
                      }
                      const newFilter = adoptionStatusFilter.filter((aStatus) => aStatus !== componentStatus)
                      setAdoptionStatusFilter(newFilter)
                    } else {
                      setAdoptionStatusFilter([...adoptionStatusFilter, componentStatus])
                    }
                  }}
                />
              )
            })}
            {adoptionStatusFilter.includes('has-been-adopted') && (
              <FilterList label="État de l'adoption" icon={<PersonPin />}>
                {[...Object.values(AnimalAdoptionDtoStep), 'empty'].map((step) => (
                  <FilterListItem
                    key={step}
                    label={step === 'empty' ? 'Vide' : getReadableAnimalAdoptionStep(step as AnimalAdoptionDtoStep)}
                    isSelected={stepFilter.includes(step as AnimalAdoptionDtoStepWithEmpty)}
                    toggleSelected={() => {
                      if (stepFilter.includes(step as AnimalAdoptionDtoStepWithEmpty)) {
                        setStepFilter(stepFilter.filter((aStep) => aStep !== step))
                      } else {
                        setStepFilter([...stepFilter, step as AnimalAdoptionDtoStepWithEmpty])
                      }
                    }}
                  />
                ))}
              </FilterList>
            )}
            {adoptionStatusFilter.includes('cannot-be-adopted') && (
              <FilterList label="Raison non-adoption" icon={<PersonPin />}>
                {[...Object.values(AnimalAdoptionDtoCannotBeAdoptedStatus), 'empty'].map((status) => (
                  <FilterListItem
                    key={status}
                    label={
                      status === 'empty'
                        ? 'Vide'
                        : getReadableCannotBeAdoptedStatus(status as AnimalAdoptionDtoCannotBeAdoptedStatus)
                    }
                    isSelected={cannotBeAdoptedStatusFilter.includes(
                      status as AnimalAdoptionDtoCannotBeAdoptedStatusWithEmpty
                    )}
                    toggleSelected={() => {
                      if (
                        cannotBeAdoptedStatusFilter.includes(status as AnimalAdoptionDtoCannotBeAdoptedStatusWithEmpty)
                      ) {
                        setCannotBeAdoptedStatusFilter(
                          cannotBeAdoptedStatusFilter.filter((aStatus) => aStatus !== status)
                        )
                      } else {
                        setCannotBeAdoptedStatusFilter([
                          ...cannotBeAdoptedStatusFilter,
                          status as AnimalAdoptionDtoCannotBeAdoptedStatusWithEmpty,
                        ])
                      }
                    }}
                  />
                ))}
              </FilterList>
            )}
          </FilterList>
        </OverviewFilterSection>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexGrow: 1,
        minHeight: 0,
        backgroundColor: 'transparent',
        boxSizing: 'border-box',
        p: 0,
        m: 0,
      }}
    >
      {animalStore.isLoading && !animalStore.areAnimalsInitialized ? (
        <CircularProgressPanel />
      ) : (
        <>
          <Card
            sx={{
              ...spacingItem,
              marginTop: 0,
              width: '100%',
              height: '100%',
              boxSizing: 'border-box',
              position: 'relative',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                px: { xs: 1.5, sm: 2.5 },
                borderBottom: '1px solid #ECEFF5',
                mb: 2,
              }}
            >
              <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                variant="scrollable"
                scrollButtons="auto"
                ref={tabsRef}
                sx={{ ...overviewTabsSx, mb: 0, px: 0, borderBottom: 'none', flex: 1, minWidth: 0 }}
              >
                <Tab label="Tous" value="all" sx={overviewTabSx} />
                <Tab label="Animaux suivis" value="followed" sx={overviewTabSx} />
                <Tab label="Animaux publiables" value="published" sx={overviewTabSx} />
                <Tab label="Statut à compléter" value="to-be-filled" sx={overviewTabSx} />
                <Tab label="Animaux archivés" value="archived" sx={overviewTabSx} />
              </Tabs>
              <Button
                component="a"
                href={associationPublicPageLink}
                target="_blank"
                rel="noreferrer"
                endIcon={<OpenInNewRounded sx={{ fontSize: 16 }} />}
                sx={{
                  display: { xs: 'none', sm: 'inline-flex' },
                  color: colors.cello,
                  fontSize: '0.9rem',
                  fontWeight: 800,
                  whiteSpace: 'nowrap',
                  minWidth: 'auto',
                  px: 0,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: colors.cello,
                    textDecoration: 'underline',
                  },
                }}
              >
                Consulter la page vitrine
              </Button>
            </Box>
            {assoHasAnimals && (
              <Box sx={{ width: '100%', px: { xs: 1.5, sm: 2.5 }, py: 2.5 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      id="name-input"
                      placeholder="Rechercher un animal (nom, nº d'immatriculation, membres...)"
                      type="text"
                      fullWidth
                      value={searchQuery}
                      onChange={onChangeSearch}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault()
                          handleApplySearch()
                        }
                      }}
                      sx={searchFieldSx}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                        endAdornment:
                          searchQuery !== '' ? (
                            <InputAdornment position="end">
                              <IconButton aria-label="Effacer la recherche" size="small" onClick={handleClearSearch}>
                                <Close fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ) : null,
                      }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                    <Button
                      variant="outlined"
                      startIcon={
                        activeFiltersCount > 0 ? (
                          <Box component="span" sx={overviewFilterCountBadgeSx}>
                            {activeFiltersCount}
                          </Box>
                        ) : (
                          <Tune fontSize="small" />
                        )
                      }
                      onClick={() => setIsFiltersOpen(true)}
                      sx={{ ...overviewActionButtonSx, flexShrink: 0, height: 40, py: 0 }}
                    >
                      Filtres
                    </Button>

                    <Button
                      variant="outlined"
                      onClick={() => handleApplySearch()}
                      disabled={!isSearchDirty}
                      startIcon={<SearchIcon />}
                      sx={{
                        whiteSpace: 'nowrap',
                        height: 40,
                        py: 0,
                        '& .MuiButton-startIcon': {
                          marginRight: 0.4,
                        },
                        ...overviewActionButtonSx,
                      }}
                    >
                      Rechercher
                    </Button>

                    <Typography
                      sx={{
                        ml: 'auto',
                        textAlign: 'right',
                        color: colors.cello,
                        fontSize: '0.78rem',
                        fontWeight: 800,
                        whiteSpace: 'nowrap',
                        minWidth: 'auto',
                        px: 0,
                      }}
                    >
                      Sélection : {selectedAnimals.length}
                    </Typography>

                    {activeFiltersCount > 0 && (
                      <Button
                        variant="text"
                        onClick={resetFilters}
                        sx={{
                          display: { xs: 'none', sm: 'inline-flex' },
                          textTransform: 'none',
                          color: '#7A8197',
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          lineHeight: 1.1,
                          whiteSpace: 'nowrap',
                          minWidth: 'auto',
                          px: 1,
                          '&:hover': {
                            backgroundColor: 'transparent',
                            color: colors.cello,
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        Reinitialiser les filtres
                      </Button>
                    )}
                  </Box>
                </Box>
                {hasAppliedSearch && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      Recherche active : {appliedSearchQuery}
                    </Typography>
                    <Button
                      size="small"
                      variant="text"
                      color="secondary"
                      onClick={handleClearSearch}
                      startIcon={<Close fontSize="small" />}
                      sx={{ textTransform: 'none' }}
                    >
                      Effacer la recherche
                    </Button>
                  </Box>
                )}
                {isSearchDirty && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                    Appuyez sur Entrée ou cliquez sur Rechercher pour lancer la recherche.
                  </Typography>
                )}
              </Box>
            )}

            {assoHasAnimals && searchQuery.trim() !== '' && (
              <Box
                sx={{
                  position: 'fixed',
                  left: '50%',
                  bottom: 16,
                  transform: 'translateX(-50%)',
                  zIndex: 1300,
                  display: { xs: 'block', sm: 'none' },
                }}
              >
                <Button
                  variant="contained"
                  onClick={() => handleApplySearch()}
                  disabled={!isSearchDirty}
                  startIcon={<SearchIcon />}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '999px',
                    px: 4,
                    height: 46,
                    boxShadow: '0 8px 22px rgba(32, 56, 100, 0.18)',
                    '& .MuiButton-startIcon': { marginRight: 0.5 },
                  }}
                >
                  Rechercher
                </Button>
              </Box>
            )}

            <OverviewFilterDialog open={isFiltersOpen} onClose={() => setIsFiltersOpen(false)} title="Filtres animaux">
              <FiltersBar />
            </OverviewFilterDialog>
            <Box
              sx={{
                width: '100%',
                flexGrow: 1,
                boxSizing: 'border-box',
                overflow: 'auto',
                paddingBottom: 7, // HACK: To avoid the last row to be hidden
                transform: 'translateZ(0)',
              }}
              ref={scrollRef}
            >
              {assoHasAnimals ? (
                animalStore.isLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    <AnimalsTable
                      animals={displayedAnimals}
                      setSortColumn={setSortColumn}
                      setSortDirection={setSortDirection}
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      nameMaxChars={17}
                      disableFrontSorting={true}
                      selectedAnimals={selectedAnimals}
                      setSelectedAnimals={setSelectedAnimals}
                    />

                    {selectedTab !== 'published' && (
                      <Pagination
                        count={Math.ceil(animalStore.animalsCount / maxAnimalPerPage)}
                        page={page}
                        onChange={(_, page) => {
                          setPage(page)
                        }}
                        sx={{ display: 'flex', justifyContent: 'center', marginTop: 2, paddingBottom: 2, mb: 8 }}
                      />
                    )}
                  </>
                )
              ) : (
                <GettingStartedPanel />
              )}
            </Box>

            {account && accountCanEdit(account) && selectedAnimals.length > 0 && (
              <Box
                sx={{
                  position: 'fixed',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  right: 2,
                  bottom: 0,
                  margin: 2,
                }}
              >
                <Fab
                  color="secondary"
                  variant="extended"
                  onClick={() =>
                    navigate(PATHS.multiEdition.absolute, {
                      state: {
                        selectedAnimals: JSON.stringify(
                          selectedAnimals.map((a) => ({
                            value: a.id,
                            label: a.name,
                            species: a.species,
                            adoptionStatus: a.adoptionStatus,
                          }))
                        ),
                      },
                    })
                  }
                >
                  Modifier les animaux sélectionnés ({selectedAnimals.length})
                </Fab>
              </Box>
            )}
          </Card>
        </>
      )}
    </Box>
  )
}
